// MiniSiteEditor.jsx — ÉDITEUR B2B du mini-site marchand (Mon compte Pro)
// ─────────────────────────────────────────────────────────────────────────────
// Le commerçant remplit sa fiche + ses produits → PUT /api/v3/me/minisite →
// visible IMMÉDIATEMENT dans le marketplace (recherche chat) et sur
// /commercant/<slug>. Palette émeraude/champagne (vit dans le panneau Pro).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const GOLD = "#EBD9A8";
const GOLD_GRAD = "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)";

const ACTIVITIES = [
  ["restauration", "🍽 Restaurant / Maquis"], ["alimentation", "🛒 Alimentation"],
  ["shopping", "👗 Mode / Vêtements"], ["electronique", "📱 Électronique"],
  ["sante", "💊 Pharmacie"], ["beaute", "💄 Beauté / Coiffure"],
  ["fleuriste", "💐 Fleuriste"], ["deco", "🛋 Décoration"],
  ["plomberie", "🔧 Plombier"], ["electricite", "⚡ Électricien"],
  ["garage", "🚗 Garagiste"], ["couture", "🧵 Couturier"],
  ["justice", "⚖️ Juridique"], ["medecine", "🩺 Santé / Médical"],
  ["autre", "📦 Autre"],
];
const PRODUCT_CATEGORIES = ["nourriture", "alimentaire", "mode", "electronique", "electromenager", "hygiene", "beaute", "pharmacie", "fleurs", "deco", "maison", "autre"];
const COMMUNES = ["Cocody", "Yopougon", "Plateau", "Treichville", "Marcory", "Abobo", "Adjamé", "Koumassi", "Port-Bouët", "Attécoubé", "Bingerville"];
const PAYMENTS = ["Wallet AYA", "Orange Money", "MTN Money", "Wave", "Espèces à la livraison", "Carte bancaire"];

const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 9,
  border: "1px solid rgba(232,213,163,0.3)", background: "rgba(4,24,15,0.6)",
  color: "#F4F1E8", fontSize: 13, fontFamily: "inherit",
};
const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: GOLD, margin: "10px 0 4px" };

const EMPTY_PRODUCT = { name: "", category: "autre", price_fcfa: "", stock: 10, emoji: "🛍", sizes: "", photo: "" };

// v3.6.1 — Compression client (canvas 1200px max, JPEG 0.85) puis upload
// POST /me/minisite/photo → URL persistante servie par /api/v3/uploads/*.
async function uploadPhotoFile(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const img = new Image();
    const fr = new FileReader();
    fr.onload = () => { img.src = fr.result; };
    fr.onerror = reject;
    img.onload = () => {
      const MAX = 1200;
      const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    fr.readAsDataURL(file);
  });
  const token = localStorage.getItem("civ_user_token");
  const r = await fetch(`${API}/api/v3/me/minisite/photo`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data: dataUrl }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.ok) throw new Error(d.error || `Erreur ${r.status}`);
  return d.url;
}

// Bouton d'upload générique avec aperçu (couverture boutique + photos produits).
function PhotoUpload({ value, onChange, label, testid, compact }) {
  const [busy, setBusy] = useState(false);
  const inputId = React.useId();
  const src = value && value.startsWith("/") ? `${API}${value}` : value;
  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadPhotoFile(file);
      onChange(url);
      toast.success("📸 Photo enregistrée");
    } catch (err) {
      toast.error(err.message === "image_too_large_5mb_max" ? "Image trop lourde (5 Mo max)" : "Upload impossible");
    } finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      {src ? (
        <img src={src} alt="" style={{ width: compact ? 38 : 64, height: compact ? 38 : 64, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(232,213,163,0.35)", flexShrink: 0 }} />
      ) : (
        <div style={{ width: compact ? 38 : 64, height: compact ? 38 : 64, borderRadius: 8, border: "1px dashed rgba(232,213,163,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: compact ? 14 : 20, color: "rgba(244,241,232,0.35)", flexShrink: 0 }}>📷</div>
      )}
      <label htmlFor={inputId} data-testid={testid} style={{
        padding: compact ? "7px 10px" : "8px 12px", borderRadius: 9, cursor: busy ? "wait" : "pointer",
        border: "1px solid rgba(232,213,163,0.45)", background: "rgba(232,213,163,0.08)",
        color: "#EBD9A8", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
      }}>
        {busy ? "Envoi…" : (value ? "Changer" : (label || "Ajouter une photo"))}
      </label>
      {value && !busy && (
        <button type="button" onClick={() => onChange("")} aria-label="Retirer la photo"
          style={{ background: "transparent", border: "none", color: "rgba(248,113,113,0.8)", cursor: "pointer", fontSize: 13, padding: 2 }}>✕</button>
      )}
      <input id={inputId} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} style={{ display: "none" }} />
    </div>
  );
}

// Libellé du bouton adapté à l'activité (owner : « Gérer ma boutique / mon restaurant / … »)
function manageLabel(activity) {
  if (activity === "restauration") return "Gérer mon restaurant";
  if (["plomberie", "electricite", "serrurerie", "garage", "couture", "menage", "froid"].includes(activity)) return "Gérer mes services";
  if (["justice", "medecine", "comptabilite", "immobilier"].includes(activity)) return "Gérer mon cabinet";
  if (activity === "sante") return "Gérer ma pharmacie";
  return "Gérer ma boutique";
}

export default function MiniSiteEditor() {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState(null);
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(true);
  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setTimeout(() => document.querySelector('[data-testid="minisite-editor"]')?.scrollIntoView({ behavior: "smooth" }), 120);
    };
    window.addEventListener("aya:open-minisite-editor", onOpen);
    return () => window.removeEventListener("aya:open-minisite-editor", onOpen);
  }, []);
  const toggleOnline = async () => {
    const next = !online;
    setOnline(next); // optimiste
    try {
      const token = localStorage.getItem("civ_user_token");
      const r = await fetch(`${API}/api/v3/me/minisite/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ online: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!d.ok) { setOnline(!next); toast.error("Statut non enregistré"); return; }
      toast.success(next ? "🟢 Tu es EN LIGNE — visible des clients" : "⚪ Hors ligne — tu ne reçois plus de commandes");
    } catch (_) { setOnline(!next); toast.error("Erreur réseau"); }
  };
  const [form, setForm] = useState({
    name: "", activity: "autre", tagline: "", emoji: "🏪", presentation: "",
    commune: "", address: "", hours: "", returns: "",
    payment: ["Wallet AYA"], delivery_fee: 1000, delivery_eta: 60, delivery_free: "",
    promo: "", cover_photo: "",
  });
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("civ_user_token");
    fetch(`${API}/api/v3/me/minisite`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        setSlug(d.slug || null);
        const ms = d.minisite;
        if (ms) {
          setOnline(ms.online !== false);
          setForm({
            name: ms.name || "", activity: ms.activity || "autre", tagline: ms.tagline || "",
            emoji: ms.emoji || "🏪", presentation: ms.presentation || "",
            commune: ms.commune || "", address: ms.address || "", hours: ms.hours || "",
            returns: ms.returns || "", payment: ms.payment && ms.payment.length ? ms.payment : ["Wallet AYA"],
            delivery_fee: ms.delivery ? ms.delivery.fee_fcfa : 1000,
            delivery_eta: ms.delivery ? ms.delivery.eta_min : 60,
            delivery_free: ms.delivery && ms.delivery.free_above_fcfa ? ms.delivery.free_above_fcfa : "",
            promo: ms.promotions && ms.promotions[0] ? ms.promotions[0].label : "",
            cover_photo: ms.cover_photo || "",
          });
          setProducts((ms.products || []).map((p) => ({
            name: p.name || "", category: p.category || "autre",
            price_fcfa: p.price_fcfa || "", stock: p.stock ?? 10,
            emoji: p.emoji || "🛍", sizes: (p.sizes || []).join(","),
            photos: Array.isArray(p.photos) ? p.photos.filter(Boolean) : [],
          })));
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setProduct = (i, k, v) => setProducts((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom de ta boutique est requis"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("civ_user_token");
      const body = {
        name: form.name, activity: form.activity, tagline: form.tagline, emoji: form.emoji,
        presentation: form.presentation, commune: form.commune || null, address: form.address || null,
        hours: form.hours || null, returns: form.returns || null, payment: form.payment,
        delivery: { zones: form.commune ? [form.commune] : [], fee_fcfa: Number(form.delivery_fee) || 0, eta_min: Number(form.delivery_eta) || 60, free_above_fcfa: form.delivery_free ? Number(form.delivery_free) : null },
        promotions: form.promo.trim() ? [{ label: form.promo.trim() }] : [],
        cover_photo: form.cover_photo || null,
        products: products
          .filter((p) => p.name.trim() && Number(p.price_fcfa) > 0)
          .map((p) => ({
            name: p.name.trim(), category: p.category, price_fcfa: Number(p.price_fcfa),
            stock: Number(p.stock) || 0, emoji: p.emoji || "🛍",
            sizes: p.sizes ? p.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
            photos: (p.photos || []).filter(Boolean).slice(0, 6),
          })),
      };
      const r = await fetch(`${API}/api/v3/me/minisite`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { toast.error(d.error || `Erreur ${r.status}`); return; }
      setSlug(d.slug);
      toast.success("Mini-site publié ✅ — tes produits sont cherchables dans le chat !");
    } catch (_) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div style={{ fontSize: 12, color: "#94a3b8", padding: 8 }}>Chargement du mini-site…</div>;

  return (
    <section data-testid="minisite-editor" style={{ background: "rgba(6,42,31,0.5)", border: "1px solid rgba(232,213,163,0.3)", borderRadius: 16, padding: "12px 14px", color: "#F4F1E8" }}>
      {/* ── En-tête permanent : bouton adaptatif + toggle en ligne + voir ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="minisite-manage-btn"
          onClick={() => setOpen((o) => !o)}
          style={{ flex: 1, minWidth: 190, padding: "11px 14px", borderRadius: 11, border: "none", background: GOLD_GRAD, color: "#3A2E10", fontWeight: 800, fontSize: 13.5, cursor: "pointer", textAlign: "left", boxShadow: "0 4px 14px rgba(205,164,82,0.3)" }}
        >
          🏬 {manageLabel(form.activity)} {open ? "▲" : "▼"}
        </button>
        <button
          type="button"
          data-testid="minisite-online-toggle"
          onClick={toggleOnline}
          style={{
            padding: "10px 14px", borderRadius: 999, fontWeight: 800, fontSize: 12, cursor: "pointer",
            border: `1px solid ${online ? "rgba(52,211,153,0.6)" : "rgba(244,241,232,0.3)"}`,
            background: online ? "rgba(16,185,129,0.18)" : "rgba(244,241,232,0.08)",
            color: online ? "#34D399" : "rgba(244,241,232,0.6)",
          }}
        >
          {online ? "🟢 En ligne" : "⚪ Hors ligne"}
        </button>
        {slug && (
          <a href={`/commercant/${slug}`} data-testid="minisite-view-link"
            style={{ fontSize: 12, fontWeight: 700, color: "#EBD9A8", border: "1px solid rgba(232,213,163,0.45)", background: "rgba(232,213,163,0.08)", padding: "9px 12px", borderRadius: 9, textDecoration: "none" }}>
            👁 Voir
          </a>
        )}
      </div>
      {!open && (
        <p style={{ fontSize: 11, color: "rgba(244,241,232,0.5)", margin: "8px 2px 0" }}>
          Fiche, produits, promos, politiques — tout ton mini-site se gère ici.
        </p>
      )}
      <div style={{ display: open ? "block" : "none", marginTop: 12 }}>
      <p style={{ fontSize: 11.5, color: "rgba(244,241,232,0.6)", margin: "0 0 4px" }}>
        Remplis ta fiche et tes produits — ils deviennent immédiatement cherchables par les clients dans le chat AYA.
      </p>

      <label style={labelStyle}>Nom de la boutique *</label>
      <input style={inputStyle} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex : Boutique Awa Cosmétiques" data-testid="ms-ed-name" />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelStyle}>Activité</label>
          <select style={inputStyle} value={form.activity} onChange={(e) => set("activity", e.target.value)} data-testid="ms-ed-activity">
            {ACTIVITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Commune</label>
          <select style={inputStyle} value={form.commune} onChange={(e) => set("commune", e.target.value)} data-testid="ms-ed-commune">
            <option value="">— Choisir —</option>
            {COMMUNES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <label style={labelStyle}>Slogan</label>
      <input style={inputStyle} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Ex : Beauté naturelle made in CI" />

      <label style={labelStyle}>Photo de couverture de la boutique</label>
      <PhotoUpload
        value={form.cover_photo}
        onChange={(url) => set("cover_photo", url)}
        label="Ajouter une photo de couverture"
        testid="ms-ed-cover-upload"
      />

      <label style={labelStyle}>Présentation</label>
      <textarea style={{ ...inputStyle, minHeight: 64, resize: "vertical" }} value={form.presentation} onChange={(e) => set("presentation", e.target.value)} placeholder="Raconte ta boutique en quelques phrases…" />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelStyle}>Adresse / repère</label>
          <input style={inputStyle} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Angré Château, rue…" />
        </div>
        <div>
          <label style={labelStyle}>Horaires</label>
          <input style={inputStyle} value={form.hours} onChange={(e) => set("hours", e.target.value)} placeholder="Lun-Sam 9h-19h" />
        </div>
      </div>

      <label style={labelStyle}>Moyens de paiement</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {PAYMENTS.map((p) => {
          const on = form.payment.includes(p);
          return (
            <button key={p} type="button"
              onClick={() => set("payment", on ? form.payment.filter((x) => x !== p) : [...form.payment, p])}
              style={{
                padding: "6px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer",
                border: `1px solid ${on ? "rgba(232,213,163,0.7)" : "rgba(244,241,232,0.2)"}`,
                background: on ? "rgba(232,213,163,0.18)" : "transparent",
                color: on ? GOLD : "rgba(244,241,232,0.6)",
              }}>
              {on ? "✓ " : ""}{p}
            </button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelStyle}>Frais livraison (F)</label>
          <input style={inputStyle} type="number" value={form.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Délai (min)</label>
          <input style={inputStyle} type="number" value={form.delivery_eta} onChange={(e) => set("delivery_eta", e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Offerte dès (F)</label>
          <input style={inputStyle} type="number" value={form.delivery_free} onChange={(e) => set("delivery_free", e.target.value)} placeholder="—" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelStyle}>Politique de retours</label>
          <input style={inputStyle} value={form.returns} onChange={(e) => set("returns", e.target.value)} placeholder="Échange sous 3 jours…" />
        </div>
        <div>
          <label style={labelStyle}>Promotion en cours</label>
          <input style={inputStyle} value={form.promo} onChange={(e) => set("promo", e.target.value)} placeholder="-20% cette semaine…" />
        </div>
      </div>

      {/* ── Produits ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 6px" }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>🛍 Mes produits ({products.length})</h4>
        <button type="button" data-testid="ms-ed-add-product"
          onClick={() => setProducts((ps) => [...ps, { ...EMPTY_PRODUCT }])}
          style={{ padding: "6px 12px", borderRadius: 9, border: "none", background: GOLD_GRAD, color: "#3A2E10", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
          ➕ Ajouter
        </button>
      </div>
      {products.length === 0 && (
        <div style={{ fontSize: 12, color: "rgba(244,241,232,0.5)", padding: "10px 0" }}>
          Aucun produit — ajoute ton premier produit pour apparaître dans les recherches !
        </div>
      )}
      {products.map((p, i) => (
        <div key={i} style={{ border: "1px dashed rgba(232,213,163,0.25)", borderRadius: 12, padding: "10px 10px 12px", marginBottom: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "48px 1fr", gap: 8 }}>
            <input style={{ ...inputStyle, textAlign: "center", padding: "9px 4px" }} value={p.emoji} onChange={(e) => setProduct(i, "emoji", e.target.value)} maxLength={4} aria-label="Emoji" />
            <input style={inputStyle} value={p.name} onChange={(e) => setProduct(i, "name", e.target.value)} placeholder="Nom du produit *" data-testid={`ms-ed-product-name-${i}`} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
            <input style={inputStyle} type="number" value={p.price_fcfa} onChange={(e) => setProduct(i, "price_fcfa", e.target.value)} placeholder="Prix (F) *" data-testid={`ms-ed-product-price-${i}`} />
            <input style={inputStyle} type="number" value={p.stock} onChange={(e) => setProduct(i, "stock", e.target.value)} placeholder="Stock" />
            <select style={inputStyle} value={p.category} onChange={(e) => setProduct(i, "category", e.target.value)}>
              {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginTop: 8, alignItems: "center" }}>
            <input style={inputStyle} value={p.sizes} onChange={(e) => setProduct(i, "sizes", e.target.value)} placeholder="Tailles (ex: 8,9,10)" />
            <button type="button" onClick={() => setProducts((ps) => ps.filter((_, idx) => idx !== i))}
              aria-label="Supprimer"
              style={{ padding: "0 12px", borderRadius: 9, border: "1px solid rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.12)", color: "#FCA5A5", cursor: "pointer", fontSize: 13 }}>
              🗑
            </button>
          </div>
          {/* v3.6.2 — GALERIE : jusqu'à 6 photos par produit (carrousel fiche publique). */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {(p.photos || []).map((url, pi) => (
              <div key={pi} style={{ position: "relative" }}>
                <img
                  src={url && url.startsWith("/") ? `${API}${url}` : url}
                  alt=""
                  style={{ width: 38, height: 38, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(232,213,163,0.35)", display: "block" }}
                />
                <button
                  type="button"
                  onClick={() => setProduct(i, "photos", (p.photos || []).filter((_, x) => x !== pi))}
                  aria-label="Retirer la photo"
                  data-testid={`ms-ed-product-photo-remove-${i}-${pi}`}
                  style={{
                    position: "absolute", top: -6, right: -6, width: 16, height: 16,
                    borderRadius: "50%", border: "none", background: "rgba(248,113,113,0.9)",
                    color: "#fff", fontSize: 10, lineHeight: 1, cursor: "pointer", padding: 0,
                  }}
                >×</button>
              </div>
            ))}
            {(p.photos || []).length < 6 && (
              <PhotoUpload
                compact
                value=""
                onChange={(url) => url && setProduct(i, "photos", [...(p.photos || []), url])}
                label={(p.photos || []).length ? "+ Photo" : "Photos"}
                testid={`ms-ed-product-photo-${i}`}
              />
            )}
          </div>
        </div>
      ))}

      <button type="button" data-testid="ms-ed-save" disabled={saving} onClick={save}
        style={{ width: "100%", marginTop: 12, padding: "12px 14px", borderRadius: 12, border: "none", background: GOLD_GRAD, color: "#3A2E10", fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 14px rgba(205,164,82,0.35)" }}>
        {saving ? "Publication…" : "💾 Publier mon mini-site"}
      </button>
      </div>
    </section>
  );
}
